import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  isPlayable,
  playableFirst,
  regionCounts,
  sameRegion,
  stationCategories,
  type CategoryId,
  type CountryConfig,
  type StationV2,
} from "@orvis/core";
import type { FirestoreLike } from "@orvis/data";
import { EmptyRow, LoadingRow, NewsCard, SectionTitle, StationCard } from "../components/ui.js";
import { InlineAd } from "../components/InlineAd.js";
import { useChannels, useNews, useRadios } from "../queries.js";
import { useFavorites, stationKey } from "../state/favorites.js";
import { useRecentlyPlayed } from "../state/recentlyPlayed.js";
import { useNowPlaying } from "../state/nowPlaying.js";
import { useNewsPrefs } from "../state/newsPrefs.js";
import { useWatchLive } from "./News.js";

/**
 * Discover — the "app in one page" tab. Sections, top to bottom:
 *   1. Recently played (TV + Radio)
 *   2. Favorites (TV + Radio)
 *   3. "Because you like <category>" — the category that dominates the
 *      user's recents + favorites, filled with stations they haven't tried
 *   4. "More from <region>" — same idea for the radio region they listen to most
 *   5. Live channels — first ~6 playable TV channels
 *   6. Featured radio — first ~6 stations, ordered by radio-browser votes
 *   7. Headlines — first 5 news items
 *
 * Personalization is on-device only (recents + favorites in localStorage);
 * nothing about listening habits leaves the phone. Everything renders from
 * the same TanStack Query cache the individual tabs use.
 */

type Kind = "tv" | "radio";
interface Pick {
  kind: Kind;
  station: StationV2;
  key: string;
}

const ROW = 6;

export function DiscoverScreen({ db, country }: { db: FirestoreLike; country: CountryConfig }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tvQ = useChannels(db, country.code);
  const radioQ = useRadios(db, country.code);
  const newsQ = useNews(db, country);
  const favorites = useFavorites();
  const recent = useRecentlyPlayed();
  const now = useNowPlaying();
  const newsPrefs = useNewsPrefs();
  const watchLive = useWatchLive(db, country, newsQ.data?.source?.sources);

  const tvAll = useMemo(() => keyed("tv", tvQ.data?.open ?? []), [tvQ.data]);
  const radioAll = useMemo(() => keyed("radio", radioQ.data?.open ?? []), [radioQ.data]);

  const recentPicks = [...pickByKeys(recent.list("tv"), tvAll), ...pickByKeys(recent.list("radio"), radioAll)];
  const favoritePicks = [...pickByKeys(favorites.list("tv"), tvAll), ...pickByKeys(favorites.list("radio"), radioAll)];
  const seen = new Set([...recentPicks, ...favoritePicks].map((p) => `${p.kind}:${p.key}`));
  const unseen = (p: Pick) => !seen.has(`${p.kind}:${p.key}`) && isPlayable(p.station);

  const topCategory = dominantCategory([...recentPicks, ...favoritePicks]);
  const forYou = topCategory
    ? [...tvAll, ...radioAll].filter((p) => unseen(p) && stationCategories(p.station).includes(topCategory)).slice(0, ROW)
    : [];

  const listenedRadios = [...recentPicks, ...favoritePicks].filter((p) => p.kind === "radio").map((p) => p.station);
  const topRegion = regionCounts(listenedRadios)[0]?.region;
  const fromRegion = topRegion
    ? radioAll.filter((p) => unseen(p) && sameRegion(p.station.region, topRegion)).slice(0, ROW)
    : [];

  const trending = playableFirst(tvAll.map((p) => p.station))
    .filter(isPlayable)
    .slice(0, ROW)
    .map((s) => tvAll.find((p) => p.station === s)!);
  const featured = radioAll.filter((p) => isPlayable(p.station)).slice(0, ROW);
  const headlines = newsPrefs.visible(newsQ.data?.items ?? []).slice(0, 5);

  const play = (p: Pick) => {
    recent.push(p.kind, p.key);
    if (p.kind === "tv") now.playTv(p.station);
    else now.playRadio(p.station);
    // The players live on their tabs — take the user to the one that plays.
    navigate(p.kind === "tv" ? "/tv" : "/radio");
  };

  const isLoading = tvQ.isPending && radioQ.isPending && newsQ.isPending;
  if (isLoading) return <LoadingRow label={t("discover")} />;

  const row = (picks: Pick[], prefix: string, withFavorite = false) => (
    <div className="space-y-2">
      {picks.map((p) => (
        <StationCard
          key={`${prefix}-${p.kind}-${p.key}`}
          station={p.station}
          showLiveBadge={p.kind === "tv"}
          subtitle={p.kind === "radio" ? p.station.region : undefined}
          onSelect={() => play(p)}
          {...(withFavorite
            ? { isFavorite: true, onToggleFavorite: () => favorites.toggle(p.kind, p.key) }
            : {})}
        />
      ))}
    </div>
  );

  return (
    <div className="p-3 space-y-6">
      <section>
        <SectionTitle>{t("recently_played")}</SectionTitle>
        {recentPicks.length === 0 ? <EmptyRow label={t("nothing_here_yet")} /> : row(recentPicks, "recent")}
      </section>

      <section>
        <SectionTitle>{t("favorites")}</SectionTitle>
        {favoritePicks.length === 0 ? <EmptyRow label={t("tap_to_favorite")} /> : row(favoritePicks, "fav", true)}
      </section>

      {forYou.length > 0 && topCategory ? (
        <section>
          <SectionTitle>{t("because_you_like", { category: t(`category_${topCategory}`) })}</SectionTitle>
          {row(forYou, "foryou")}
        </section>
      ) : null}

      {fromRegion.length > 0 && topRegion ? (
        <section>
          <SectionTitle>
            <Link to={`/radio?region=${encodeURIComponent(topRegion)}`} className="hover:text-text">
              {t("more_from_region", { region: topRegion })}
            </Link>
          </SectionTitle>
          {row(fromRegion, "region")}
        </section>
      ) : null}

      {trending.length > 0 ? (
        <section>
          <SectionTitle>
            <Link to="/tv" className="hover:text-text">{t("trending_channels")}</Link>
          </SectionTitle>
          {row(trending, "live")}
        </section>
      ) : null}

      {trending.length > 0 ? <InlineAd country={country} slot="discover" /> : null}

      {featured.length > 0 ? (
        <section>
          <SectionTitle>
            <Link to="/radio" className="hover:text-text">{t("featured_radios")}</Link>
          </SectionTitle>
          {row(featured, "feat")}
        </section>
      ) : null}

      {headlines.length > 0 ? (
        <section>
          <SectionTitle>
            <Link to="/news" className="hover:text-text">{t("headlines")}</Link>
          </SectionTitle>
          <div className="space-y-2">
            {headlines.map((n) => (
              <NewsCard key={n.id} item={n} action={watchLive(n)} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function keyed(kind: Kind, list: readonly StationV2[]): Pick[] {
  return list.map((station, i) => ({ kind, station, key: stationKey(station, i) }));
}

function pickByKeys(keys: readonly string[], all: readonly Pick[]): Pick[] {
  const byKey = new Map(all.map((p) => [p.key, p]));
  return keys.map((k) => byKey.get(k)).filter((p): p is Pick => p !== undefined);
}

/**
 * The category that best describes what the user plays. "general" is too
 * vague to recommend from, so it only wins when nothing else shows up.
 */
function dominantCategory(picks: readonly Pick[]): CategoryId | undefined {
  const counts = new Map<CategoryId, number>();
  for (const p of picks) for (const c of stationCategories(p.station)) counts.set(c, (counts.get(c) ?? 0) + 1);
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
  return ranked.find((c) => c !== "general") ?? ranked[0];
}
